/** @typedef {import('headlight-configurable-application').Pict} Pict */
const PictProvider = require('pict-provider');

class LocationProvider extends PictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
	}

	/**
	 * Get the Locations by Sample ID
	 * @param {Number} pIDSample - The ID of the Sample to get Locations for
	 * @param {Function} [pCallback] - The callback function to call when the Location is fetched
	 * @return {Promise}
	 * @async
	 */
	async getLocationsBySampleID(pIDSample, pCallback)
	{
		try
		{
			return await new Promise((resolve, reject) =>
			{
				// First check if there is already a join between these.
				this.pict.fable.services.HeadlightRestClient.getJSON(`SampleLocationJoins/FilteredTo/FBV~IDSample~EQ~${ pIDSample }~FSF~CreateDate~DESC~0`,
					async (pError, pBody) =>
					{
						if (pError)
						{
							this.pict.log.error(`Error fetching Locations: `, pError);
							reject(pError);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(null);
							}
						}
						else
						{
							if (pBody.length)
							{
								const fullLocations = [];
								for (const j of pBody)
								{
									fullLocations.push(await this.getLocationByID(j.IDLocation));
								}
								resolve(fullLocations);
								if (pCallback)
								{
									pCallback(fullLocations);
								}
							}
							else
							{
								resolve([]);
								if (pCallback)
								{
									pCallback([]);
								}
							}
						}
					});
			});
		}
		catch (error)
		{
			this.pict.log.error(`Error fetching Locations: `, error);
			// if we have a callback, run it now
			if (pCallback)
			{
				pCallback(null);
			}
		}
	}

	/**
	 * Get the Location by GUID
	 * @param {Number} pGUIDLocation - The GUID of the Location to get
	 * @param {Function} [pCallback] - The callback function to call when the Location is fetched
	 * @param {Number} [recursiveDepth] - The depth of current recursion
	 * @return {Promise}
	 * @async
	 */
	async getLocationByGUID(pGUIDLocation, pCallback, recursiveDepth = 0)
	{
		const maxRecursiveDepth = 1;
		try
		{
			return await new Promise((resolve, reject) =>
			{
				this.pict.fable.services.HeadlightRestClient.getJSON(`Locations/By/GUIDLocation/${ pGUIDLocation }`,
					async (pError, pBody) =>
					{
						if (pError)
						{
							this.pict.log.error(`Error fetching Location: `, pError);
							reject(pError);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(null);
							}
						}
						else
						{
							pBody = pBody[0];
							if (pBody.ParentIDLocation && recursiveDepth < maxRecursiveDepth)
							{
								try
								{
									pBody.ParentLocation = await this.getLocationByID(pBody.ParentIDLocation, null, recursiveDepth + 1);
								}
								catch (pError)
								{
									this.pict.log.error(`Error fetching Location: `, pError);
									reject(pError);
									// if we have a callback, run it now
									if (pCallback)
									{
										pCallback(null);
									}
								}
							}
							resolve(pBody);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(pBody);
							}
						}
					});
			});
		}
		catch (error)
		{
			this.pict.log.error(`Error fetching Location: `, error);
			// if we have a callback, run it now
			if (pCallback)
			{
				pCallback(null);
			}
		}
	}

	/**
	 * Get the Location by ID
	 * @param {Number} pIDLocation - The ID of the Location to get
	 * @param {Function} [pCallback] - The callback function to call when the Location is fetched
	 * @param {Number} [recursiveDepth] - The depth of current recursion
	 * @return {Promise}
	 * @async
	 */
	async getLocationByID(pIDLocation, pCallback, recursiveDepth = 0)
	{
		const maxRecursiveDepth = 1;
		try
		{
			return await new Promise((resolve, reject) =>
			{
				this.pict.fable.services.HeadlightRestClient.getJSON(`Location/${ pIDLocation }`,
					async (pError, pBody) =>
					{
						if (pError)
						{
							this.pict.log.error(`Error fetching Location: `, pError);
							reject(pError);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(null);
							}
						}
						else
						{
							if (pBody.ParentIDLocation && recursiveDepth < maxRecursiveDepth)
							{
								try
								{
									pBody.ParentLocation = await this.getLocationByID(pBody.ParentIDLocation, null, recursiveDepth + 1);
								}
								catch (pError)
								{
									this.pict.log.error(`Error fetching Location: `, pError);
									reject(pError);
									// if we have a callback, run it now
									if (pCallback)
									{
										pCallback(null);
									}
								}
							}
							resolve(pBody);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(pBody);
							}
						}
					});
			});
		}
		catch (error)
		{
			this.pict.log.error(`Error fetching Location: `, error);
			// if we have a callback, run it now
			if (pCallback)
			{
				pCallback(null);
			}
		}
	}

	/**
	 * Add a sample to a location.
	 * @param {Number} pIDSample - The ID of the Sample to add to the location
	 * @param {Number} pIDLocation - The ID of the Location to add the Sample to
	 * @param {Function} [pCallback] - The callback function to call when the action is complete
	 * @return {Promise}
	 * @async
	 */
	async addSampleToLocation(pIDSample, pIDLocation, pCallback)
	{
		try
		{
			return await new Promise((resolve, reject) =>
			{
				// First check if there is already a join between these.
				this.pict.fable.services.HeadlightRestClient.getJSON(`SampleLocationJoins/FilteredTo/FBV~IDSample~EQ~${ pIDSample }~FSF~CreateDate~DESC~0`,
					async (pError, pBody) =>
					{
						if (pError)
						{
							this.pict.log.error(`Error adding sample to location: `, pError);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(null);
								return;
							}
							reject(pError);
						}
						else
						{
							// If we found a join already, return it.
							if (pBody?.length && pBody[0].IDLocation == pIDLocation)
							{
								resolve(pBody[0]);
								// if we have a callback, run it now
								if (pCallback)
								{
									pCallback(pBody[0]);
								}
							}
							else
							{
								// Otherwise, create the join and return that.
								this.pict.HeadlightRestClient.putJSON('SampleLocationJoin/Upsert', { IDSample: pIDSample, IDLocation: pIDLocation }, (pError, pBody) =>
								{
									if (pError)
									{
										this.pict.log.error(`Error adding sample to location: `, pError);
										// if we have a callback, run it now
										if (pCallback)
										{
											pCallback(null);
											return;
										}
										reject(pError);
									}
									else
									{
										this.pict.HeadlightRestClient.getJSON(`Sample/${ pIDSample }`, async (pError, pBody) =>
										{
											if (pError)
											{
												this.pict.log.error(`Error adding sample to location: `, pError);
												// if we have a callback, run it now
												if (pCallback)
												{
													pCallback(null);
													return;
												}
												reject(pError);
											}
											else
											{
												if (parseInt(pBody?.Extension?.StorageLocation) !== pIDLocation)
												{
													this.pict.HeadlightRestClient.putJSON('Sample',
													{
														IDSample: pIDSample,
														Extension: Object.assign(pBody.Extension, { StorageLocation: `${ pIDLocation }` }),
													}, async (pError, pBody) =>
													{
														if (pError)
														{
															this.pict.log.error(`Error adding sample to location: `, pError);
															// if we have a callback, run it now
															if (pCallback)
															{
																pCallback(null);
																return;
															}
															reject(pError);
														}
														else
														{
															resolve(pBody);
															// if we have a callback, run it now
															if (pCallback)
															{
																pCallback(pBody);
															}
														}
													});
												}
												else
												{
													resolve(pBody);
													// if we have a callback, run it now
													if (pCallback)
													{
														pCallback(pBody);
													}
												}
											}
										});
									}
								});
							}
						}
					});
			});
		}
		catch (error)
		{
			this.pict.log.error(`Error adding Sample to Location: `, error);
			// if we have a callback, run it now
			if (pCallback)
			{
				pCallback(null);
			}
		}
	}

	/**
	 * Remove a sample from a location.
	 * @param {Number} pIDSample - The ID of the Sample to remove from the location
	 * @param {Number} pIDLocation - The ID of the Location to remove the Sample from
	 * @param {Function} [pCallback] - The callback function to call when the action is complete
	 * @return {Promise}
	 * @async
	 */
	async removeSamplefromLocation(pIDSample, pIDLocation, pCallback)
	{
		try
		{
			return await new Promise((resolve, reject) =>
			{
				// First check if there is already a join between these.
				this.pict.fable.services.HeadlightRestClient.getJSON(`SampleLocationJoins/FilteredTo/FBV~IDSample~EQ~${ pIDSample }~FBV~IDLocation~EQ~${ pIDLocation }`,
					async (pError, pBody) =>
					{
						if (pError)
						{
							this.pict.log.error(`Error removing sample from location: `, pError);
							reject(pError);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(null);
							}
						}
						else
						{
							// If we found a join already, delete it.
							if (pBody?.length)
							{
								this.pict.HeadlightRestClient.deleteEntity('SampleLocationJoin', pBody[0].IDSampleLocationJoin, (pError, pBody) =>
								{
									if (pError)
									{
										this.pict.log.error(`Error removing sample from location: `, pError);
										reject(pError);
										// if we have a callback, run it now
										if (pCallback)
										{
											pCallback(null);
										}
									}
									else
									{
										resolve(true);
										// if we have a callback, run it now
										if (pCallback)
										{
											pCallback(true);
										}
									}
								});
							}
							else
							{
								// Otherwise, just return;
								resolve(true);
								// if we have a callback, run it now
								if (pCallback)
								{
									pCallback(true);
								}
							}
						}
					});
			});
		}
		catch (error)
		{
			this.pict.log.error(`Error removing Sample from Location: `, error);
			// if we have a callback, run it now
			if (pCallback)
			{
				pCallback(null);
			}
		}
	}
}

module.exports = LocationProvider;
module.exports.default_configuration = {};
